import {Injectable, Injector} from '@angular/core';
import {Auth, authState, signOut} from '@angular/fire/auth';
import {Capacitor} from '@capacitor/core';
import {FirebaseAuthentication, User} from '@capacitor-firebase/authentication';
import {parsePhoneNumber} from 'awesome-phonenumber';
import {Router} from '@angular/router';
import {BehaviorSubject, EMPTY, Observable, firstValueFrom, from, of, throwError} from 'rxjs';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import {ContextProvider} from '@core/providers';
import {PhoneNumberLoginService} from '@core/services/phone-number-login.service';
import {needsBasicProfile} from '@core/utils/needs-basic-profile.util';
import {isStaleConsumerAuth} from '@core/utils/stale-consumer-auth.util';
import {getErrorMessage} from '@core/utils/api-error-handler';
import {EmailAlreadyExistError} from '@shared/errors';
import {
  ConsumerAccountVehicleApiModel,
  ConsumerAccountVehicleListApiModel,
  ConsumerMeApiModel,
  ConsumerProfileApiModel,
  ConsumerSessionModel,
  EnsureConsumerRequest,
  PhoneNumberModel,
  UpdateConsumerProfileRequest,
} from 'spotcharge-types';
import {BaseOccpHttpService} from './base-occp-http.service';
import {StorageService} from './storage.service';

const CONSUMER_ACCOUNT_NOT_FOUND = 'CONSUMER_ACCOUNT_NOT_FOUND';
const CONSUMER_ACCOUNT_DELETED = 'CONSUMER_ACCOUNT_DELETED';
const CONSUMER_EMAIL_TAKEN = 'CONSUMER_EMAIL_TAKEN';
const EMAIL_ALREADY_USED_MESSAGE =
  'This email address is already associated with a different account. Please use a different email.';
const CONSUMER_SESSION_KEY = 'consumerSession';

@Injectable({
  providedIn: 'root'
})
export class UserHttpService {
  user$: Observable<ConsumerSessionModel | null>;
  private sessionRefresh$ = new BehaviorSubject<void>(undefined);
  private sessionSubject = new BehaviorSubject<ConsumerSessionModel | null>(null);
  /** Bumped on logout so an in-flight save cannot restore a cleared session. */
  private sessionGeneration = 0;
  private persistQueue: Promise<void> = Promise.resolve();
  private refreshFlight: Promise<ConsumerSessionModel | null> | null = null;
  private redirectIfIncomplete = false;

  constructor(
    private angularFireAuth: Auth,
    public phoneNumberLoginService: PhoneNumberLoginService,
    public contextProvider: ContextProvider,
    private occpHttp: BaseOccpHttpService,
    private storageService: StorageService,
    private injector: Injector,
  ) {
    this.user$ = this.sessionSubject.asObservable();
    // A transient null from Firebase during cold start must not wipe a seeded session.
    this.phoneNumberLoginService.currentUser$.pipe(
      switchMap((user) => {
        if (!user) {
          return EMPTY;
        }
        return this.sessionRefresh$.pipe(
          switchMap(() => from(this.refreshFromNetwork(user)).pipe(switchMap(() => EMPTY))),
        );
      }),
    ).subscribe();
  }

  refreshSession(): void {
    this.sessionRefresh$.next();
  }

  async readCachedSession(): Promise<ConsumerSessionModel | null> {
    const raw = await this.storageService.get(CONSUMER_SESSION_KEY);
    return isUsableCachedSession(raw) ? raw : null;
  }

  /** Show the map from the last session and refresh the account without blocking navigation. */
  openFromCache(firebaseUser: User, session: ConsumerSessionModel): void {
    this.publish(session, this.sessionGeneration);
    this.redirectIfIncomplete = true;
    void this.refreshFromNetwork(firebaseUser);
  }

  async ensureSession(firebaseUser?: User | null): Promise<ConsumerSessionModel | null> {
    const user = firebaseUser
      ?? await this.phoneNumberLoginService.getCurrentUser()
      ?? (await FirebaseAuthentication.getCurrentUser()).user;
    if (!user) {
      return null;
    }
    return this.refreshFromNetwork(user);
  }

  async clearStoredSession(): Promise<void> {
    this.sessionGeneration += 1;
    this.sessionSubject.next(null);
    const generation = this.sessionGeneration;
    this.enqueuePersist(async () => {
      if (generation !== this.sessionGeneration) {
        return;
      }
      await this.storageService.remove('user');
      await this.storageService.remove(CONSUMER_SESSION_KEY);
    });
    await this.persistQueue;
  }

  async updateProfile(input: UpdateConsumerProfileRequest): Promise<ConsumerProfileApiModel> {
    try {
      const profile = await firstValueFrom(
        this.occpHttp.patch<ConsumerProfileApiModel>('api/consumer/me', input),
      );
      this.refreshSession();
      return profile;
    } catch (err: any) {
      if (isEmailTakenError(err)) {
        throw new EmailAlreadyExistError(EMAIL_ALREADY_USED_MESSAGE);
      }
      throw err;
    }
  }

  async getUser(): Promise<ConsumerSessionModel | null> {
    return firstValueFrom(this.user$.pipe(take(1)));
  }

  async getAccessToken() {
    const user = await firstValueFrom(authState(this.angularFireAuth).pipe(take(1)));
    return user?.getIdTokenResult(true);
  }

  async getCustomClaims(){
    const user = await firstValueFrom(authState(this.angularFireAuth).pipe(take(1)));
    const tokenResult = await user?.getIdTokenResult();
    return (tokenResult && tokenResult.claims && tokenResult.claims.roles) ? tokenResult.claims.roles : null;
  }

  private async signOutFirebase(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      await signOut(this.angularFireAuth);
      return;
    }
    await FirebaseAuthentication.signOut();
  }

  private async handleLoadError(err: unknown): Promise<void> {
    if (isStaleConsumerAuth(err as {code?: string; status?: number})) {
      await this.clearStoredSession();
      await this.signOutFirebase();
      return;
    }
    console.error('Failed to load consumer', err);
  }

  private refreshFromNetwork(firebaseUser: User): Promise<ConsumerSessionModel | null> {
    if (this.refreshFlight) {
      return this.refreshFlight;
    }
    const sessionEpoch = this.sessionGeneration;
    this.refreshFlight = firstValueFrom(this.loadConsumer(firebaseUser, sessionEpoch))
      .then(async (consumer) => {
        if (!needsBasicProfile(consumer)) {
          this.redirectIfIncomplete = false;
          return consumer;
        }
        if (this.redirectIfIncomplete) {
          this.redirectIfIncomplete = false;
          const router = this.injector.get(Router);
          if (!router.url.includes('/set-basic-profile')) {
            await router.navigate(['/set-basic-profile']);
          }
        }
        return consumer;
      })
      .catch(async (err) => {
        await this.handleLoadError(err);
        return null;
      })
      .finally(() => {
        this.refreshFlight = null;
      });
    return this.refreshFlight;
  }

  private publish(session: ConsumerSessionModel, sessionEpoch: number): void {
    if (sessionEpoch !== this.sessionGeneration) {
      return;
    }
    this.sessionSubject.next(session);
    this.contextProvider.storeUserData({id: session.profile.firebaseUid, name: session.profile.firstName});
    this.enqueuePersist(async () => {
      if (sessionEpoch !== this.sessionGeneration) {
        return;
      }
      await this.storageService.set(CONSUMER_SESSION_KEY, session);
      if (sessionEpoch !== this.sessionGeneration) {
        await this.storageService.remove(CONSUMER_SESSION_KEY);
      }
    });
  }

  /** One failed write must not block a later logout clear. */
  private enqueuePersist(task: () => Promise<void>): void {
    this.persistQueue = this.persistQueue
      .catch(() => undefined)
      .then(task)
      .catch(() => undefined);
  }

  private loadConsumer(firebaseUser: User, sessionEpoch: number): Observable<ConsumerSessionModel> {
    return this.occpHttp.get<ConsumerMeApiModel>('api/consumer/me').pipe(
      catchError((err) => {
        if (err?.code === CONSUMER_ACCOUNT_DELETED) {
          return throwError(() => err);
        }
        if (err?.code === CONSUMER_ACCOUNT_NOT_FOUND) {
          return this.occpHttp.post<ConsumerMeApiModel>(
            'api/consumer/me/ensure',
            this.toEnsureRequest(firebaseUser),
          ).pipe(
            catchError((ensureErr) => {
              if (ensureErr?.code === CONSUMER_ACCOUNT_DELETED) {
                return throwError(() => ensureErr);
              }
              return throwError(() => ensureErr);
            }),
          );
        }
        return throwError(() => err);
      }),
      switchMap((me) => this.withVehicles(me)),
      tap((session) => this.publish(session, sessionEpoch)),
    );
  }

  private withVehicles(me: ConsumerMeApiModel): Observable<ConsumerSessionModel> {
    return this.occpHttp.get<ConsumerAccountVehicleListApiModel>('api/consumer/me/vehicles').pipe(
      map((list) => this.toConsumerSession(me, list.rows)),
      catchError(() => of(this.toConsumerSession(me))),
    );
  }

  private toEnsureRequest(user: User): EnsureConsumerRequest {
    const displayName = (user.displayName ?? '').trim();
    const [firstName, ...rest] = displayName.split(/\s+/);
    return {
      firstName: firstName || undefined,
      lastName: rest.length ? rest.join(' ') : undefined,
      email: user.email || undefined,
      phoneNumber: toStoredPhoneNumber(user.phoneNumber),
      photoUrl: user.photoUrl || (user as {photoURL?: string | null}).photoURL || undefined,
    };
  }

  private toConsumerSession(
    me: ConsumerMeApiModel,
    vehicles: ConsumerAccountVehicleApiModel[] = [],
  ): ConsumerSessionModel {
    const {profile, account, wallet} = me;
    this.contextProvider.storeUserData({id: profile.firebaseUid, name: profile.firstName});
    return {
      profile,
      account,
      wallet,
      isNewAccount: me.isNewAccount,
      vehicles,
    };
  }
}

export function profileSaveErrorMessage(error: unknown): string {
  if (error instanceof EmailAlreadyExistError) {
    return error.message;
  }
  return getErrorMessage(error);
}

function isEmailTakenError(err: {code?: string; status?: number; message?: string} | null | undefined): boolean {
  if (err?.code === CONSUMER_EMAIL_TAKEN) {
    return true;
  }
  return err?.status === 409 && /email/i.test(err.message ?? '');
}

function isUsableCachedSession(value: unknown): value is ConsumerSessionModel {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const session = value as ConsumerSessionModel;
  return typeof session.profile?.firebaseUid === 'string'
    && typeof session.profile?.isProfileComplete === 'boolean'
    && typeof session.isNewAccount === 'boolean'
    && Array.isArray(session.vehicles);
}

export function toStoredPhoneNumber(value: unknown): PhoneNumberModel | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'object') {
    const phone = value as PhoneNumberModel;
    return phone.number?.e164 ? phone : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const parsed = parsePhoneNumber(value);
  return parsed.valid ? parsed as unknown as PhoneNumberModel : null;
}
